import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import AppLayout from '../components/layout/AppLayout';
import { getAllUsers, registerUser, toggleUserStatus, editUser, changeUserPassword, deleteUser, uploadFile, getGroups } from '../services/api';

const ROLE_COLORS = { admin: '#ef4444', consultant: '#f59e0b', user: '#3b82f6' };
const ROLE_BG     = { admin: '#fef2f2',  consultant: '#fffbeb',  user: '#eff6ff'  };
const initials    = (name) => name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

/* shared field style */
const fieldStyle = {
  width: '100%', padding: '10px 13px',
  border: '1.5px solid var(--border)',
  borderRadius: 10, fontSize: 13.5,
  color: 'var(--text-main)', background: 'var(--bg-input)',
  outline: 'none', fontFamily: 'inherit',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const FieldFocus   = e => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = '0 0 0 3px var(--primary-light)'; };
const FieldBlur    = e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; };

const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Dot = ({ c }) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />;

/* Icon-trigger + styled dropdown menu (replaces native <select>) */
function FancySelect({ value, onChange, options, placeholder, leadingIcon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        ...fieldStyle, display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
        borderColor: open ? 'var(--primary)' : 'var(--border)',
        boxShadow: open ? '0 0 0 3px var(--primary-light)' : 'none',
      }}>
        {leadingIcon && <span style={{ display: 'flex', color: 'var(--text-muted)', flexShrink: 0 }}>{leadingIcon}</span>}
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected && selected.value !== '' ? 'var(--text-main)' : 'var(--text-muted)' }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ color: 'var(--text-muted)', display: 'flex' }}><ChevronIcon open={open} /></span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 'auto',
          minWidth: '100%', width: 'max-content', maxWidth: 360, zIndex: 1200,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.16)', padding: 6, maxHeight: 300, overflowY: 'auto',
        }}>
          {options.map(o => {
            const active = String(o.value) === String(value);
            return (
              <button key={o.value || '__all'} type="button"
                onMouseDown={() => { onChange(o.value); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '9px 11px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  background: active ? 'var(--primary-light)' : 'transparent',
                  color: active ? 'var(--primary)' : 'var(--text-main)',
                  fontSize: 13.5, fontWeight: active ? 600 : 500, fontFamily: 'inherit', textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.04))'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 18, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, color: 'var(--text-muted)' }}>{o.icon}</span>
                <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{o.label}</span>
                {active && <span style={{ color: 'var(--primary)', display: 'flex', flexShrink: 0 }}><CheckIcon /></span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MODULE_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
  </svg>
);

/* ─────────────────────────────────────────────────
   AVATAR PREVIEW
───────────────────────────────────────────────── */
function AvatarPreview({ name, role, avatarUrl, onChange }) {
  const color = ROLE_COLORS[role] || '#6b7280';
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await uploadFile(fd);
      onChange?.(data.url);
    } catch (err) {
      Swal.fire('Upload failed', err.response?.data?.error || 'Could not upload the image.', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 0 4px' }}>
      <button type="button" onClick={pick} title="Click to upload a photo"
        style={{
          position: 'relative', width: 72, height: 72, borderRadius: '50%', padding: 0, cursor: 'pointer',
          background: avatarUrl ? '#fff' : `linear-gradient(135deg, ${color}cc, ${color})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 800, color: 'white', overflow: 'hidden',
          boxShadow: `0 8px 24px ${color}40`, border: `3px solid ${color}30`,
        }}>
        {avatarUrl
          ? <img src={avatarUrl} alt={name || 'avatar'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (initials(name) || '?')}
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 8.5, fontWeight: 700,
          padding: '2px 0', letterSpacing: 0.3,
        }}>{uploading ? '…' : '📷 EDIT'}</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPick} />
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', textTransform: 'capitalize' }}>
        {name || 'New User'} · <span style={{ color }}>{role}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   ROLE SELECTOR CARDS
───────────────────────────────────────────────── */
const ROLES = [
  { id: 'user',       label: 'User',       icon: '👤', desc: 'Submit & track tickets' },
  { id: 'consultant', label: 'Consultant', icon: '💼', desc: 'Resolve & manage tickets' },
  { id: 'admin',      label: 'Admin',      icon: '🛡️', desc: 'Full system access' },
];

function RoleSelector({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
      {ROLES.map(r => {
        const active = value === r.id;
        return (
          <button key={r.id} type="button" onClick={() => onChange(r.id)}
            style={{
              padding: '10px 8px', border: `2px solid ${active ? ROLE_COLORS[r.id] : 'var(--border)'}`,
              borderRadius: 12, background: active ? ROLE_BG[r.id] : 'var(--bg-card)',
              cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
              boxShadow: active ? `0 4px 12px ${ROLE_COLORS[r.id]}25` : 'none',
            }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{r.icon}</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: active ? ROLE_COLORS[r.id] : 'var(--text-main)' }}>{r.label}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>{r.desc}</div>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   CREATE USER MODAL
───────────────────────────────────────────────── */
function CreateModal({ onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user', department: '', phone: '', avatar_url: '', group_id: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [groups, setGroups] = useState([]);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  // Department/Section dropdown drives both group_id (routing) and the
  // department label (display) so existing department UI keeps working.
  const onGroupPick = (e) => {
    const id = e.target.value;
    const g = groups.find(x => x.id === id);
    setForm(p => ({ ...p, group_id: id, department: g ? g.name : '' }));
  };

  useEffect(() => { getGroups().then(r => setGroups(r.data || [])).catch(() => {}); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await registerUser(form);
      onClose(); onRefresh();
      Swal.fire({ icon: 'success', title: 'User Created!', timer: 1500, showConfirmButton: false, customClass: { popup: 'swal-custom' } });
    } catch (error) {
      setErr(error.response?.data?.error || 'Failed to create user.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540, animation: 'modalIn 0.2s ease' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))',
          borderRadius: '12px 12px 0 0', padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>Add New User</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Create a new account in the system</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: 'white', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {err && <div className="alert alert-error">{err}</div>}

            {/* Avatar preview */}
            <AvatarPreview name={form.name} role={form.role} avatarUrl={form.avatar_url} onChange={url => setForm(p => ({ ...p, avatar_url: url }))} />

            {/* Role selector */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-sub)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 8 }}>Select Role</div>
              <RoleSelector value={form.role} onChange={v => setForm(p => ({ ...p, role: v }))} />
            </div>

            {/* Name + Email */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={fieldStyle} value={form.name} onChange={f('name')}
                  placeholder="e.g. John Doe" required onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="email" style={fieldStyle} value={form.email} onChange={f('email')}
                  placeholder="john@company.com" required onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input type={showPwd ? 'text' : 'password'} style={{ ...fieldStyle, paddingRight: 40 }}
                  value={form.password} onChange={f('password')}
                  placeholder="Min. 6 characters" required onFocus={FieldFocus} onBlur={FieldBlur} />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  {showPwd
                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18 18 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18 18 0 0 1-2.16 3.19"/></svg>
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            {/* Department/Section + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Department / Section</label>
                <FancySelect
                  leadingIcon={MODULE_ICON}
                  placeholder="— Select module —"
                  value={form.group_id}
                  onChange={v => onGroupPick({ target: { value: v } })}
                  options={[
                    { value: '', label: '— Select module —', icon: MODULE_ICON },
                    ...groups.map(g => ({ value: g.id, label: g.name, icon: <Dot c="var(--primary)" /> })),
                  ]}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Phone</label>
                <input style={fieldStyle} value={form.phone} onChange={f('phone')}
                  placeholder="+91 98765 43210" onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '14px 24px 20px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}
              style={{ minWidth: 120, background: 'linear-gradient(135deg,#0284c7,#38bdf8)', border: 'none' }}>
              {saving ? 'Creating…' : '✓ Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   EDIT DETAILS MODAL
───────────────────────────────────────────────── */
function EditModal({ user, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: user.name, email: user.email, department: user.department || '', phone: user.phone || '', avatar_url: user.avatar_url || '', group_id: user.group_id || '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [groups, setGroups] = useState([]);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const onGroupPick = (e) => {
    const id = e.target.value;
    const g = groups.find(x => x.id === id);
    setForm(p => ({ ...p, group_id: id, department: g ? g.name : '' }));
  };

  useEffect(() => { getGroups().then(r => setGroups(r.data || [])).catch(() => {}); }, []);

  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await uploadFile(fd);
      setForm(p => ({ ...p, avatar_url: data.url }));
    } catch (error) {
      Swal.fire('Upload failed', error.response?.data?.error || 'Could not upload the image.', 'error');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await editUser(user.id, form);
      onClose(); onRefresh();
      Swal.fire({ icon: 'success', title: 'Updated!', timer: 1400, showConfirmButton: false, customClass: { popup: 'swal-custom' } });
    } catch (error) {
      setErr(error.response?.data?.error || 'Failed to update.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500, animation: 'modalIn 0.2s ease' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))',
          borderRadius: '12px 12px 0 0', padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>Edit Details</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Update information for {user.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: 'white', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {err && <div className="alert alert-error">{err}</div>}

            {/* Current user badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--bg-hover)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <button type="button" onClick={() => avatarInputRef.current?.click()} title="Click to change photo"
                style={{ position: 'relative', width: 44, height: 44, borderRadius: '50%', padding: 0, cursor: 'pointer', overflow: 'hidden', flexShrink: 0,
                  background: form.avatar_url ? '#fff' : `linear-gradient(135deg,${ROLE_COLORS[user.role]}cc,${ROLE_COLORS[user.role]})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: 'white', border: 'none' }}>
                {form.avatar_url
                  ? <img src={form.avatar_url} alt={form.name || user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : initials(form.name || user.name)}
                <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 7, fontWeight: 700, padding: '1px 0' }}>{avatarUploading ? '…' : '📷'}</span>
              </button>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarPick} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{form.name || user.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{user.email} · <span style={{ color: ROLE_COLORS[user.role], textTransform: 'capitalize', fontWeight: 600 }}>{user.role}</span></div>
              </div>
            </div>

            {/* Name + Email */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={fieldStyle} value={form.name} onChange={f('name')}
                  required onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="email" style={fieldStyle} value={form.email} onChange={f('email')}
                  required onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
            </div>

            {/* Department/Section + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Department / Section</label>
                <FancySelect
                  leadingIcon={MODULE_ICON}
                  placeholder="— Select module —"
                  value={form.group_id}
                  onChange={v => onGroupPick({ target: { value: v } })}
                  options={[
                    { value: '', label: '— Select module —', icon: MODULE_ICON },
                    ...groups.map(g => ({ value: g.id, label: g.name, icon: <Dot c="var(--primary)" /> })),
                  ]}
                />
                {user.role === 'consultant' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>
                    Tickets for this module can be routed to this consultant.
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Phone</label>
                <input style={fieldStyle} value={form.phone} onChange={f('phone')}
                  placeholder="+91 98765 43210" onFocus={FieldFocus} onBlur={FieldBlur} />
              </div>
            </div>
          </div>

          <div style={{ padding: '14px 24px 20px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}
              style={{ minWidth: 120, background: 'linear-gradient(135deg,#0284c7,#0ea5e9)', border: 'none' }}>
              {saving ? 'Saving…' : '✓ Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   CHANGE PASSWORD MODAL
───────────────────────────────────────────────── */
function PasswordModal({ user, onClose }) {
  const [pwd,  setPwd]   = useState('');
  const [pwd2, setPwd2]  = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err,  setErr]   = useState('');

  const match = pwd && pwd2 && pwd === pwd2 && pwd.length >= 6;

  const handleSave = async (e) => {
    e.preventDefault();
    if (pwd.length < 6)  return setErr('Password must be at least 6 characters.');
    if (pwd !== pwd2)    return setErr('Passwords do not match.');
    setSaving(true); setErr('');
    try {
      await changeUserPassword(user.id, { password: pwd });
      onClose();
      Swal.fire({ icon: 'success', title: 'Password Updated!', timer: 1500, showConfirmButton: false, customClass: { popup: 'swal-custom' } });
    } catch (error) {
      setErr(error.response?.data?.error || 'Failed.');
    } finally { setSaving(false); }
  };

  const EyeBtn = ({ show, toggle }) => (
    <button type="button" onClick={toggle}
      style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
      {show
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18 18 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18 18 0 0 1-2.16 3.19"/></svg>
        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      }
    </button>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420, animation: 'modalIn 0.2s ease' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#f59e0b,#f97316)',
          borderRadius: '12px 12px 0 0', padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>Change Password</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>Reset credentials for {user.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: 'white', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {err && <div className="alert alert-error">{err}</div>}

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>New Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input type={show1 ? 'text' : 'password'} style={{ ...fieldStyle, paddingRight: 40 }}
                  value={pwd} onChange={e => setPwd(e.target.value)}
                  placeholder="Min. 6 characters" required onFocus={FieldFocus} onBlur={FieldBlur} />
                <EyeBtn show={show1} toggle={() => setShow1(s => !s)} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 6 }}>Confirm Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input type={show2 ? 'text' : 'password'} style={{ ...fieldStyle, paddingRight: 40, borderColor: pwd2 ? (match ? '#10b981' : '#ef4444') : 'var(--border)' }}
                  value={pwd2} onChange={e => setPwd2(e.target.value)}
                  placeholder="Repeat new password" required onFocus={FieldFocus} onBlur={FieldBlur} />
                <EyeBtn show={show2} toggle={() => setShow2(s => !s)} />
              </div>
            </div>

            {/* Strength hint */}
            {pwd2 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: match ? '#10b981' : '#ef4444' }}>
                {match ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}

            {/* Strength bar */}
            {pwd && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>Password strength</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[1,2,3,4].map(i => (
                    <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: pwd.length >= i * 3 ? (pwd.length >= 10 ? '#10b981' : pwd.length >= 7 ? '#f59e0b' : '#ef4444') : 'var(--border)' }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '14px 24px 20px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={saving}
              style={{ minWidth: 140, background: 'linear-gradient(135deg,#f59e0b,#f97316)', border: 'none', color: 'white', fontWeight: 600 }}>
              {saving ? 'Updating…' : '🔒 Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   ACTIONS DROPDOWN
───────────────────────────────────────────────── */
function ActionsMenu({ user, onEdit, onPassword, onRefresh }) {
  const [open,    setOpen]    = useState(false);
  const [above,   setAbove]   = useState(false);
  const [pos,     setPos]     = useState({ top: 0, bottom: 0, right: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setAbove(window.innerHeight - r.bottom < 260);
      setPos({
        top:    r.bottom + 6,
        bottom: window.innerHeight - r.top + 6,
        right:  window.innerWidth - r.right,
      });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          (!menuRef.current || !menuRef.current.contains(e.target))) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const close = () => setOpen(false);

  const handleToggle = async () => {
    close();
    const label = user.is_active ? 'Set Inactive' : 'Set Active';
    const result = await Swal.fire({
      title: label, icon: 'warning', showCancelButton: true,
      text: user.is_active ? `Disable ${user.name}'s access?` : `Re-enable ${user.name}'s access?`,
      confirmButtonText: label,
      confirmButtonColor: user.is_active ? '#ef4444' : '#10b981',
      cancelButtonColor: '#6b7280', customClass: { popup: 'swal-custom' },
    });
    if (!result.isConfirmed) return;
    try { await toggleUserStatus(user.id); onRefresh(); Swal.fire({ icon: 'success', title: 'Done!', timer: 1200, showConfirmButton: false, customClass: { popup: 'swal-custom' } }); } catch {}
  };

  const handleDelete = async () => {
    close();
    const result = await Swal.fire({
      title: 'Delete User?', icon: 'warning', showCancelButton: true,
      text: `"${user.name}" will be permanently removed.`,
      confirmButtonText: 'Yes, Delete', confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280', customClass: { popup: 'swal-custom' },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteUser(user.id); onRefresh();
      Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1200, showConfirmButton: false, customClass: { popup: 'swal-custom' } });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed.', customClass: { popup: 'swal-custom' } });
    }
  };

  const items = [
    { label: 'Edit Details', sub: 'Update name & email', color: 'var(--primary)', bg: 'var(--primary-light)', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{color:'var(--primary)'}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>, onClick: () => { close(); onEdit(user); } },
    { label: user.is_active ? 'Set Inactive' : 'Set Active', sub: user.is_active ? 'Disable user access' : 'Enable user access', color: user.is_active ? '#ef4444' : '#10b981', bg: user.is_active ? '#ef444412' : '#10b98112', icon: user.is_active ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>, onClick: handleToggle },
    { label: 'Change Password', sub: 'Reset credentials', color: '#f59e0b', bg: '#f59e0b12', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, onClick: () => { close(); onPassword(user); } },
    { label: 'Delete User', sub: 'Remove permanently', color: '#ef4444', bg: '#ef444412', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>, onClick: handleDelete },
  ];

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={openMenu} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 13px', background: open ? 'var(--primary)' : 'var(--bg-card)', color: open ? 'white' : 'var(--text-main)', border: `1px solid ${open ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', boxShadow: open ? '0 4px 14px var(--primary-light)' : 'none', whiteSpace: 'nowrap' }}>
        Actions
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && createPortal(
        <div ref={menuRef} style={{
          position: 'fixed',
          ...(above ? { bottom: pos.bottom } : { top: pos.top }),
          right: pos.right,
          width: 235,
          zIndex: 9999,
          background: 'var(--bg-topbar)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          overflow: 'hidden',
          animation: 'dropIn 0.18s ease',
        }}>
          <div style={{ padding: '10px 14px 9px', borderBottom: '1px solid var(--border)', background: 'var(--bg-table-head)' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-sub)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>⚡ Actions — {user.name.split(' ')[0].toUpperCase()}</span>
          </div>
          {items.map((item, i) => (
            <button key={i} onClick={item.onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', borderBottom: i < items.length - 1 ? '1px solid var(--border-light)' : 'none', transition: 'background 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{item.sub}</div>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────── */
export default function UsersPage() {
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editUser_,    setEditUser_]    = useState(null);
  const [pwdUser,      setPwdUser]      = useState(null);
  const [success,      setSuccess]      = useState('');
  const [viewMode,     setViewMode]     = useState('card');
  const [search,       setSearch]       = useState('');
  const [roleFilter,   setRoleFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchUsers = useCallback(() => {
    setLoading(true);
    getAllUsers().then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const admins       = users.filter(u => u.role === 'admin');
  const consultants  = users.filter(u => u.role === 'consultant');
  const regularUsers = users.filter(u => u.role === 'user');

  const filteredUsers = users.filter(u => {
    if (search) {
      const q = search.toLowerCase();
      if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q) && !u.department?.toLowerCase().includes(q)) return false;
    }
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter === 'active' && !u.is_active) return false;
    if (statusFilter === 'inactive' && u.is_active) return false;
    return true;
  });

  return (
    <AppLayout pageTitle="User Management">
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <p className="text-muted text-sm">{users.length} total users</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users…"
              style={{ ...fieldStyle, width: 190, paddingLeft: 30, paddingTop: 8, paddingBottom: 8 }}
              onFocus={FieldFocus} onBlur={FieldBlur} />
          </div>
          {/* Role + Status filters (card view only) */}
          {viewMode === 'card' && (
            <>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                style={{ ...fieldStyle, width: 'auto', paddingTop: 8, paddingBottom: 8 }}
                onFocus={FieldFocus} onBlur={FieldBlur}>
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="consultant">Consultant</option>
                <option value="user">User</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ ...fieldStyle, width: 'auto', paddingTop: 8, paddingBottom: 8 }}
                onFocus={FieldFocus} onBlur={FieldBlur}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </>
          )}
          {/* View toggle */}
          <button onClick={() => { setViewMode(v => v === 'classic' ? 'card' : 'classic'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              background: viewMode === 'card' ? 'var(--primary)' : 'var(--bg-card)',
              color: viewMode === 'card' ? 'white' : 'var(--text-main)',
              border: `1.5px solid ${viewMode === 'card' ? 'var(--primary)' : 'var(--border)'}`,
            }}>
            {viewMode === 'card' ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                Classic View
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                </svg>
                Card View
              </>
            )}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>➕ Add User</button>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Admins',      count: admins.length,       color: '#ef4444', icon: '🛡️' },
          { label: 'Consultants', count: consultants.length,  color: '#f59e0b', icon: '🧑‍💻' },
          { label: 'End Users',   count: regularUsers.length, color: '#3b82f6', icon: '👤' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '18px 20px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{s.icon} {s.label}</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-main)' }}>{s.count}</div>
          </div>
        ))}
      </div>

      {/* Classic Table View */}
      {viewMode === 'classic' && (
        <div className="card">
          {loading ? (
            <div className="flex-center" style={{ padding: 60 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, idx) => (
                    <tr key={u.id} style={{ cursor: 'default' }}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', background: `linear-gradient(135deg,${ROLE_COLORS[u.role]}cc,${ROLE_COLORS[u.role]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                              {u.avatar_url
                                ? <img src={u.avatar_url} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : initials(u.name)}
                            </div>
                            <span title={u.is_online ? 'Online' : 'Offline'}
                              style={{ position: 'absolute', bottom: 1, right: 1, width: 9, height: 9, borderRadius: '50%', background: u.is_online ? '#10b981' : '#9ca3af', border: '2px solid var(--bg-card)' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-main)' }}>{u.name}</div>
                            <div style={{ fontSize: 10, color: u.is_online ? '#10b981' : 'var(--text-muted)', textTransform: 'capitalize', fontWeight: u.is_online ? 600 : 400 }}>
                              {u.is_online ? '● Online' : u.role}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-sub)', fontSize: 12.5 }}>{u.email}</td>
                      <td><span style={{ background: ROLE_COLORS[u.role] + '20', color: ROLE_COLORS[u.role], padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{u.role}</span></td>
                      <td style={{ fontSize: 12.5, color: 'var(--text-sub)' }}>{u.department || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: u.is_active ? '#065f46' : '#991b1b', background: u.is_active ? '#d1fae5' : '#fee2e2', padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.is_active ? '#10b981' : '#ef4444', flexShrink: 0, display: 'inline-block' }} />
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td style={{ overflow: 'visible', position: 'relative' }}><ActionsMenu user={u} onEdit={setEditUser_} onPassword={setPwdUser} onRefresh={fetchUsers} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Card Grid View */}
      {viewMode === 'card' && (
        loading ? (
          <div className="flex-center" style={{ padding: 60 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ fontSize: 14 }}>No users found</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 16 }}>
            {filteredUsers.map(u => {
              const color = ROLE_COLORS[u.role] || '#6b7280';
              return (
                <div key={u.id} className="user-card card-3d" style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18,
                  overflow: 'hidden', boxShadow: '0 4px 16px rgba(15,23,42,0.05)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}>
                  {/* Header: gradient rounded thumbnail + name + status dot + actions */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13, padding: '16px 16px 15px',
                    background: `linear-gradient(135deg, ${color}14, transparent 70%)` }}>
                    <div style={{
                      width: 60, height: 60, borderRadius: 16, flexShrink: 0, overflow: 'hidden',
                      background: `linear-gradient(135deg, ${color}, ${color}aa)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, fontWeight: 800, color: '#fff', boxShadow: `0 8px 18px ${color}45`,
                    }}>
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initials(u.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        {u.role}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.2, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 11.5, color: 'var(--text-sub)' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                      <span title={u.is_online ? 'Online' : 'Offline'}
                        style={{ width: 10, height: 10, borderRadius: '50%', background: u.is_online ? '#10b981' : '#cbd5e1', flexShrink: 0 }} />
                      <ActionsMenu user={u} onEdit={setEditUser_} onPassword={setPwdUser} onRefresh={fetchUsers} />
                    </div>
                  </div>

                  {/* Footer: labelled columns (Role / Status / Joined) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderTop: '1px solid var(--border-light)' }}>
                    {/* Role */}
                    <div style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 7 }}>Role</div>
                      <span style={{ background: color + '1f', color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{u.role}</span>
                    </div>
                    {/* Status */}
                    <div style={{ padding: '12px 10px', textAlign: 'center', borderLeft: '1px solid var(--border-light)', borderRight: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 7 }}>Status</div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: u.is_active ? '#065f46' : '#991b1b', background: u.is_active ? '#d1fae5' : '#fee2e2', padding: '3px 10px', borderRadius: 20 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.is_active ? '#10b981' : '#ef4444' }} />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {/* Joined */}
                    <div style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 7 }}>Joined</div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)' }}>{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Modals */}
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onRefresh={fetchUsers} />}
      {editUser_  && <EditModal   user={editUser_} onClose={() => setEditUser_(null)} onRefresh={fetchUsers} />}
      {pwdUser    && <PasswordModal user={pwdUser} onClose={() => setPwdUser(null)} />}

      <style>{`
        @keyframes dropIn { from { opacity:0; transform:translateY(-6px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        .swal-custom { border-radius: 18px !important; font-family: 'DM Sans', sans-serif !important; }
        .user-card:hover { border-color: var(--primary); }
      `}</style>
    </AppLayout>
  );
}
